import { useMemo, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ShieldCheck, Gamepad2, Coins } from 'lucide-react';
import { Icon, type IconName } from '@/components/ui/icon.js';
import { Dialog, DialogContent, DialogTitle, DialogClose } from '@/components/ui/dialog.js';
import { ORIGINALS_ART } from './catalog.js';
import { originalThemeGradient, originalThemeStyle } from './theme.js';
import type { OriginalsConfig, OriginalsGameType } from './types.js';

interface Props {
  config: OriginalsConfig;
  open: boolean;
  onClose: () => void;
}

const HOW_KEY: Record<OriginalsGameType, string> = {
  wheel: 'guide.wheel.how',
  scratch: 'guide.scratch.how',
  cards: 'guide.cards.how',
  dice: 'guide.dice.how',
  keno: 'guide.keno.how',
  plinko: 'guide.plinko.how',
  mines: 'guide.mines.how',
};

function formatBpMultiplier(multiplierBp: number): string {
  const x = multiplierBp / 10000;
  // Trim trailing zeros: 25000 → "2.5x", 20000 → "2x".
  const text = Number.isInteger(x) ? String(x) : x.toFixed(2).replace(/\.?0+$/, '');
  return `${text}x`;
}

function wheelMultipliers(presenterConfig: unknown): string[] {
  const ring =
    presenterConfig != null &&
    typeof presenterConfig === 'object' &&
    'ring' in presenterConfig
      ? (presenterConfig as { ring?: unknown }).ring
      : undefined;
  if (!Array.isArray(ring)) return [];
  const labels = ring
    .map((slice) =>
      slice != null &&
      typeof slice === 'object' &&
      typeof (slice as { label?: unknown }).label === 'string'
        ? (slice as { label: string }).label
        : '',
    )
    .filter((label) => label.length > 0);
  return [...new Set(labels)];
}

function dicePayouts(): string[] {
  // headline odds for the marquee bet types (floor(0.945·216/win)/10000)
  const f = (win: number) => `${(Math.floor(0.945 * (216 / win) * 10000) / 10000).toFixed(2)}x`;
  return [`Hi/Lo ${f(105)}`, `Total ${f(27)}-${f(3)}`, `Pair ${f(16)}`, `Triple ${f(1)}`];
}

function plinkoBucketLabels(presenterConfig: unknown): string[] {
  const buckets =
    presenterConfig != null &&
    typeof presenterConfig === 'object' &&
    'buckets' in presenterConfig
      ? (presenterConfig as { buckets?: unknown }).buckets
      : undefined;
  if (!Array.isArray(buckets)) return [];
  const uniq = [...new Set(buckets.filter((b): b is number => typeof b === 'number'))].sort((a, b) => b - a);
  return uniq.map(formatBpMultiplier);
}

function cardsTiers(presenterConfig: unknown): number[] {
  const raw =
    presenterConfig != null &&
    typeof presenterConfig === 'object' &&
    'tiers' in presenterConfig
      ? (presenterConfig as { tiers?: unknown }).tiers
      : undefined;
  if (!Array.isArray(raw)) return [];
  const values = raw
    .map((tier) =>
      tier != null &&
      typeof tier === 'object' &&
      typeof (tier as { multiplierBp?: unknown }).multiplierBp === 'number'
        ? (tier as { multiplierBp: number }).multiplierBp
        : null,
    )
    .filter((v): v is number => v != null);
  return [...new Set(values)].sort((a, b) => b - a);
}

function SectionTitle({ icon: I, children }: { icon: ComponentType<{ size?: number; className?: string }>; children: ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-[14px] font-extrabold text-[var(--text-primary)]">
      <I size={17} className="text-[var(--primary)]" />
      {children}
    </h3>
  );
}

function FairRow({ term, desc }: { term: string; desc: string }) {
  return (
    <div className="flex flex-col gap-0.5 py-2.5 border-b border-[var(--border-subtle)] last:border-b-0">
      <span className="text-[12.5px] font-bold text-[var(--text-primary)]">{term}</span>
      <span className="text-[12.5px] leading-relaxed text-[var(--text-secondary)]">{desc}</span>
    </div>
  );
}

export function HowToPlay({ config, open, onClose }: Props) {
  const { t } = useTranslation('originals');

  const payouts = useMemo<string[]>(() => {
    if (config.gameType === 'wheel') return wheelMultipliers(config.presenterConfig);
    if (config.gameType === 'cards')
      return cardsTiers(config.presenterConfig).map(formatBpMultiplier);
    if (config.gameType === 'plinko') return plinkoBucketLabels(config.presenterConfig);
    if (config.gameType === 'dice') return dicePayouts();
    if (config.gameType === 'keno') return []; // falls through to the generic note
    if (config.gameType === 'mines') return []; // multiplier depends on chosen mines + reveals
    return [];
  }, [config.gameType, config.presenterConfig]);

  const art = ORIGINALS_ART[config.gameType] ?? {
    icon: 'play' as IconName,
    grad: 'var(--art-1)',
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        hideClose
        aria-label={t('guide.title')}
        // The guide portals out of the game's themed wrapper; carry the theme with it.
        style={originalThemeStyle(config.gameType)}
        className="max-w-[640px] gap-0 overflow-hidden border-[var(--border)] p-0 shadow-[0_24px_64px_rgba(0,0,0,0.45)]"
      >
        {/* Art header — gives the game a visual identity at a glance. */}
        <header
          className="relative shrink-0 overflow-hidden"
          style={{ background: originalThemeGradient(config.gameType) ?? art.grad }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" aria-hidden />
          <div className="relative flex items-center gap-4 px-5 py-5">
            <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
              <Icon name={art.icon} size={34} className="text-white" />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/75">
                {t('guide.title')}
              </div>
              <DialogTitle className="truncate text-[24px] font-extrabold leading-tight text-white">{config.name}</DialogTitle>
            </div>
          </div>
          <DialogClose
            aria-label={t('guide.close')}
            className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-xl bg-black/30 text-white/90 transition-colors hover:bg-black/50 hover:text-white"
          >
            <X size={18} aria-hidden />
          </DialogClose>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-5">
              {/* How to play — the headline, given a highlighted card. */}
              <section className="flex flex-col gap-3 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 sm:p-5">
                <SectionTitle icon={Gamepad2}>{t('guide.howToPlay')}</SectionTitle>
                <p className="text-[15px] leading-relaxed text-[var(--text-secondary)]">
                  {t(HOW_KEY[config.gameType])}
                </p>
              </section>

              {/* Payouts */}
              <section className="flex flex-col gap-3">
                <SectionTitle icon={Coins}>{t('guide.payouts')}</SectionTitle>
                {payouts.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {payouts.map((label) => (
                      <span
                        key={label}
                        className="inline-flex items-center h-9 px-3.5 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[13px] font-bold text-[var(--text-primary)] bf-tnum"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
                    {t(
                      config.gameType === 'keno'
                        ? 'guide.keno.payoutNote'
                        : config.gameType === 'mines'
                          ? 'guide.mines.payoutNote'
                          : 'guide.scratch.payoutNote'
                    )}
                  </p>
                )}
              </section>

              {/* Provably fair explainer */}
              <section className="flex flex-col gap-3 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border)] p-4 sm:p-5">
                <SectionTitle icon={ShieldCheck}>{t('guide.fair.title')}</SectionTitle>
                <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
                  {t('guide.fair.intro')}
                </p>

                <div className="flex flex-col">
                  <FairRow term={t('fairness.serverSeedHash')} desc={t('guide.fair.serverSeed')} />
                  <FairRow term={t('fairness.nextServerSeedHash')} desc={t('guide.fair.nextServerSeed')} />
                  <FairRow term={t('fairness.clientSeed')} desc={t('guide.fair.clientSeed')} />
                  <FairRow term={t('fairness.nonce')} desc={t('guide.fair.nonce')} />
                </div>

                <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
                  {t('guide.fair.formulaNote')}
                </p>
                <pre className="overflow-x-auto rounded-xl bg-[var(--bg-raised)] border border-[var(--border)] px-3.5 py-3 font-mono text-[11.5px] leading-relaxed text-[var(--text-primary)] whitespace-pre">
                  {'result = HMAC_SHA256(server_seed, client_seed + ":" + nonce)\n       → float[0,1) → outcome'}
                </pre>

                <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
                  {t('guide.fair.verifyNote', { panel: t('fairness.title') })}
                </p>
              </section>
            </div>
        </DialogContent>
      </Dialog>
  );
}
